/**
 * External dependencies
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

/**
 * Internal dependencies
 */
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import { CloudinaryImage } from '@art-of-hospitality-solutions-llc/ui-kit/components/cloudinary-image';
import ImageUploadForm from '@Components/ImageUploadForm';
import { AspectRatio } from '@Components/AspectRatio';
import { Skeleton } from '@Components/Skeleton';
import { useQueryClient } from '@tanstack/react-query';
import { CheckIcon } from 'lucide-react';
import { useAssets } from '../../hooks/useAssets';
import { cn } from '../../utils/cn';

export interface ManageGalleryImagesDialogProps {
  /** Display name shown in the dialog title */
  name: string;
  /** Currently selected asset IDs */
  currentAssetIds: string[];
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Called when the dialog should open or close */
  onOpenChange: (open: boolean) => void;
  /**
   * Called with the selected asset IDs when the user confirms.
   * Should return a promise that resolves when the save is complete.
   */
  onSave: (assetIds: string[]) => Promise<void>;
}

const FormSchema = z.object({
  assetIds: z.array(z.string()),
});

type FormValues = z.infer<typeof FormSchema>;

export default function ManageGalleryImagesDialog({
  name,
  currentAssetIds,
  isOpen,
  onOpenChange,
  onSave,
}: ManageGalleryImagesDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const { data: assets, status } = useAssets({
    enabled: isOpen,
  });

  const queryClient = useQueryClient();

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      assetIds: currentAssetIds,
    },
  });

  const rootError = form.formState.errors.root?.message;

  const handleOpenChange = (open: boolean) => {
    onOpenChange(open);
    form.reset({ assetIds: currentAssetIds });
    form.clearErrors('root');
  };

  const toggleAsset = useCallback(
    (id: string) => {
      const current = form.getValues('assetIds') ?? [];
      const next = current.includes(id)
        ? current.filter((x) => x !== id)
        : [...current, id];
      form.setValue('assetIds', next, { shouldDirty: true, shouldTouch: true });
    },
    [form],
  );

  const isSelected = useCallback(
    (id: string, selectedIds: string[] | undefined) =>
      !!selectedIds && selectedIds.includes(id),
    [],
  );

  const handleUploadSuccess = (result?: any) => {
    queryClient.invalidateQueries({ queryKey: ['assets'] });

    const newAssets = Array.isArray(result) ? result : result?.assets;
    if (Array.isArray(newAssets) && newAssets.length > 0) {
      const current = form.getValues('assetIds') ?? [];
      const newIds = newAssets
        .map((a: any) => a.id)
        .filter((id: string) => id && !current.includes(id));
      if (newIds.length > 0) {
        form.setValue('assetIds', [...current, ...newIds], {
          shouldDirty: true,
          shouldTouch: true,
        });
      }
    }
  };

  const onSubmit = async (values: FormValues) => {
    form.clearErrors('root');

    try {
      setIsSaving(true);
      await onSave(values.assetIds);
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error saving gallery images:', error);
      form.setError('root', {
        type: 'server',
        message: 'We were unable to update the images. Please try again.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Manage images for {name}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {rootError && (
              <div className="mb-4">
                <Alert variant="destructive">
                  <AlertTitle>Something went wrong</AlertTitle>
                  <AlertDescription>{rootError}</AlertDescription>
                </Alert>
              </div>
            )}

            {/* Upload */}
            <div>
              <div className="mb-2 text-sm font-medium text-zinc-700">
                Upload new images
              </div>
              <ImageUploadForm
                inputId="gallery-image-upload-input"
                onUploadSuccess={handleUploadSuccess}
              />
            </div>

            {/* Media Library */}
            <div>
              <div className="mb-2 text-sm font-medium text-zinc-700">
                Media Library
              </div>
              <FormField
                control={form.control}
                name="assetIds"
                render={({ field }) => {
                  const selectedIds = field.value ?? [];
                  return (
                    <FormItem>
                      <FormControl>
                        <div className="grid max-h-128 grid-cols-3 gap-4 overflow-y-auto p-1">
                          {status === 'pending' ? (
                            <>
                              <Skeleton className="aspect-square w-full" />
                              <Skeleton className="aspect-square w-full" />
                              <Skeleton className="aspect-square w-full" />
                            </>
                          ) : !assets || assets.length === 0 ? (
                            <div className="col-span-3 flex h-32 flex-col items-center justify-center rounded border border-dashed border-zinc-300 bg-zinc-100 text-center text-sm text-zinc-500">
                              <div className="font-medium text-zinc-700">
                                No uploaded images
                              </div>
                            </div>
                          ) : (
                            assets.map((asset) => {
                              const selected = isSelected(
                                asset.id,
                                selectedIds,
                              );
                              const tagNames =
                                asset.tags?.map((t) => t.tag.name) ?? [];
                              return (
                                <button
                                  key={asset.id}
                                  type="button"
                                  onClick={() => toggleAsset(asset.id)}
                                  className={cn(
                                    'relative flex w-full flex-col overflow-hidden rounded-md border bg-zinc-200 transition',
                                    selected
                                      ? 'border-primary ring-4 ring-[#CAAB6A]'
                                      : 'border-transparent hover:border-zinc-300',
                                  )}
                                >
                                  <AspectRatio
                                    ratio={1}
                                    className="relative w-full"
                                  >
                                    <CloudinaryImage
                                      publicId={asset.cloudinaryId}
                                      mode="original"
                                      className="h-full w-full object-contain"
                                    />
                                    {selected && (
                                      <div className="absolute top-2 right-2 flex h-6 w-6 items-center justify-center rounded-full border-2 border-[#CAAB6A] bg-black">
                                        <CheckIcon className="h-4 w-4 text-[#CAAB6A]" />
                                      </div>
                                    )}
                                  </AspectRatio>
                                  {tagNames.length > 0 && (
                                    <div className="flex flex-wrap gap-1 bg-white px-1.5 py-1.5">
                                      {tagNames.map((name) => (
                                        <span
                                          key={name}
                                          className="bg-secondary text-secondary-foreground rounded-md px-1.5 py-0.5 text-[10px] font-medium"
                                        >
                                          {name}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </button>
                              );
                            })
                          )}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            </div>

            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="link" disabled={isSaving}>
                  Cancel
                </Button>
              </DialogClose>

              <Button
                type="submit"
                variant="primary"
                loading={isSaving}
                disabled={isSaving}
              >
                Save images
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
